/**
 * The record page's tab row (#11426; shared since #13370 round 39): the strip of a record's tabs
 * under its identity strip, the active one in ink with the 2px ink underline, and an end at the
 * row's right (the Appearance editor's controls). At the desktop tier the row is one line: the
 * tabs that fit are drawn in order and the rest are listed under "More" (#14753); on a phone the
 * strip scrolls sideways inside its own hairline. The record page draws it, and so does the
 * Appearance editor, which draws the record page itself with its editing controls added (Hunter,
 * 2026-10-20: "it should look extremely similar to the page as it exists, just with all the
 * necessary controls added for editing"); one component, so the two cannot drift.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
  type MutableRefObject,
  type ReactNode,
  type Ref,
} from "react";
import { CaretDown } from "@phosphor-icons/react";
import { QuietScrollStrip } from "@/components/common/QuietScrollStrip";
import { Menu, type MenuAction } from "@/components/FpPages/RMS/design";
import { useIsPhoneViewport } from "@/hooks/useViewportTier";
import { heldFactCount } from "./Overview/previewData";
import { overflowedTabKeys, type TabRoom } from "./recordTabFit";
import { relationshipCount } from "./Tabs/relationshipCount";
import type { EntitySchema } from "./types";

export interface RecordTabRowTab {
  key: string;
  label: string;
  /** The count after the label (a record's facts, a dataset's rows). */
  count?: number;
  /** Drawn in the faintest ink: a tab with nothing in it. */
  faint?: boolean;
  /** Drawn after the label and count inside the tab (the pending-review badge). */
  trail?: ReactNode;
  /**
   * A state the tab shows on the row (its pending-review badge), said in words after its count
   * when the tab is listed under More, where the trail is not drawn (#14753).
   */
  note?: string;
}

/** What a host adds to a tab's button: the editor's tab semantics and drag handle. */
export type RecordTabButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  ref?: Ref<HTMLButtonElement>;
  [data: `data-${string}`]: string | undefined;
};

export interface RecordTabRowProps {
  tabs: readonly RecordTabRowTab[];
  /** The active tab's key. */
  active: string;
  onSelect: (key: string) => void;
  /** The nav's accessible name. */
  label?: string;
  /**
   * The tabs as a tablist named `label` (the editor's, which its keyboard selection needs): a box
   * of their own inside the nav holding the tabs alone, the nav's last item (`after`) outside it.
   */
  tablist?: boolean;
  /** The nav's own style on top of the row's (the strip held invisible while it settles). */
  navStyle?: CSSProperties;
  /** The nav element itself (a drop target's ref). */
  navRef?: Ref<HTMLElement>;
  /** More attributes on the nav (a drop target's props). */
  navProps?: HTMLAttributes<HTMLElement> & {
    [data: `data-${string}`]: string | undefined;
  };
  /** The nav's last items, after the tabs, scrolling with them (a drop target's placeholder). */
  after?: ReactNode;
  /**
   * The row's action ("+ Log interaction", "+ Add tab"), pinned right after the strip, outside
   * its scroll, so it is always in view however many tabs the strip holds (#13370 round 42).
   */
  action?: ReactNode;
  /** The row's right end, outside the scrolling strip, on the strip's own rule. */
  end?: ReactNode;
  /** More attributes on a tab's button. */
  tabProps?: (tab: RecordTabRowTab, active: boolean) => RecordTabButtonProps;
  /**
   * Wraps a tab's button (the editor's drag and its ⋯). `overflowed` says the row has no room for
   * the tab and lists it under More instead: a wrapper then hides itself the way the button is
   * hidden (`recordTabOverflowedStyle`), so it takes no room either (#14753). `drawn` is the keys
   * of the tabs the row draws, in row order (the editor's drag counts only those).
   */
  wrapTab?: (
    tab: RecordTabRowTab,
    button: ReactNode,
    index: number,
    overflowed: boolean,
    drawn: readonly string[]
  ) => ReactNode;
  /**
   * Keeps the tabs the row draws as they are, whatever their room says, while true: the editor
   * holds them for the length of a carry, so no tab is drawn or put under More mid-drag.
   */
  holdFit?: boolean;
  /** More attributes on the row. */
  rowProps?: HTMLAttributes<HTMLDivElement> & {
    [data: `data-${string}`]: string | undefined;
  };
}

/**
 * The counts a record's tabs carry from the record alone: its held facts (a zero there dims rather
 * than hides, Facts being where fields are filled) and its links. A count is a promise of a
 * number, so a tab whose count would need a read shows none (#11426).
 */
export const recordTabCounts = (
  entity: EntitySchema
): { facts: number; entities: number } => ({
  facts: heldFactCount(entity),
  entities: relationshipCount(entity),
});

/** A tab's look: sentence case, ink and the underline on the active one. */
export const recordTabStyle = (
  active: boolean,
  faint: boolean
): CSSProperties => ({
  fontFamily: "var(--fp-font-ui)",
  fontSize: "var(--fp-t-body)",
  fontWeight: active
    ? ("var(--fp-w-strong)" as unknown as number)
    : ("var(--fp-w-primary)" as unknown as number),
  letterSpacing: "var(--fp-track-body)",
  color: active
    ? "var(--fp-ink)"
    : faint
      ? "var(--fp-ink-4)"
      : "var(--fp-ink-3)",
  padding: "var(--fp-gap-tight) 0",
  marginRight: "var(--fp-gap-wide)",
  borderBottom: `2px solid ${active ? "var(--fp-ink)" : "transparent"}`,
});

/** The count after a tab's label. */
const tabCountStyle: CSSProperties = {
  fontFeatureSettings: "var(--fp-feat-num)",
  fontSize: "var(--fp-t-meta)",
  color: "var(--fp-ink-4)",
};

/** Marks a tab's button (and a host's wrapper around it) that the row lists under More. */
export const RECORD_TAB_OVERFLOWED_ATTRIBUTE = "data-overflowed";

/**
 * A tab the row has no room for (#14753): out of the flow and invisible, out of the accessibility
 * tree and the pointer's way, but still mounted and measurable, so a wider row can bring it back.
 * Never `display: none`, which would leave nothing to measure. A host's wrapper around the tab
 * takes the same style.
 */
export const recordTabOverflowedStyle: CSSProperties = {
  position: "absolute",
  left: 0,
  top: 0,
  visibility: "hidden",
  pointerEvents: "none",
};

/**
 * A quiet text action in the nav's own type ("+ Log interaction", "+ Add tab"). It has a tab's box,
 * the 2px bottom rule included (transparent), so the nav's stretched row centres its text on the
 * tabs' text line rather than a pixel below it (#13370 round 41).
 */
export const recordTabActionStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  background: "none",
  borderWidth: "0 0 2px",
  borderStyle: "solid",
  borderColor: "transparent",
  cursor: "pointer",
  fontFamily: "var(--fp-font-ui)",
  fontSize: "var(--fp-t-body)",
  color: "var(--fp-ink-3)",
  padding: "var(--fp-gap-tight) 0",
};

/** The desktop row's action comes after every tab, whatever `order` a host gives its tabs. */
const ACTION_ORDER = 1_000_000;
/** More comes after every tab and before the action. */
const MORE_ORDER = ACTION_ORDER - 1;
/** Marks the box that holds the tabs inside the nav when they are a tablist. */
const TABLIST_ATTRIBUTE = "data-record-tablist";

/** Tabs laid out as the nav's own items (`display: contents`) inside their tablist. */
const tablistBox = (label: string, items: ReactNode): ReactNode => (
  <div
    role="tablist"
    aria-label={label}
    style={{ display: "contents" }}
    {...{ [TABLIST_ATTRIBUTE]: "" }}
  >
    {items}
  </div>
);

/** An element's horizontal margins. */
const marginsOf = (element: HTMLElement): number => {
  const style = getComputedStyle(element);
  return (
    (parseFloat(style.marginLeft) || 0) + (parseFloat(style.marginRight) || 0)
  );
};

/** The room an item of the strip takes: its box and its margins. */
const roomOf = (element: HTMLElement | null): number =>
  element ? element.getBoundingClientRect().width + marginsOf(element) : 0;

/**
 * The room a tab takes in the strip: its item (the strip's own child that holds it, the button
 * itself or a host's wrapper, which carries the gap) with that item's margins. An overflowed
 * wrapper is out of the flow and shrinks to nothing around its hidden button, so the button's own
 * box stands in for it.
 */
const tabRoom = (button: HTMLElement, strip: HTMLElement): number => {
  let item: HTMLElement = button;
  while (
    item.parentElement &&
    item.parentElement !== strip &&
    !item.parentElement.hasAttribute(TABLIST_ATTRIBUTE)
  )
    item = item.parentElement;
  const buttonRoom = roomOf(button);
  if (item === button) return buttonRoom;
  return (
    Math.max(item.getBoundingClientRect().width, buttonRoom) + marginsOf(item)
  );
};

const sameKeys = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((key, index) => key === b[index]);

const NO_KEYS: readonly string[] = [];

export function RecordTabRow({
  tabs,
  active,
  onSelect,
  label = "Record sections",
  tablist = false,
  navStyle,
  navRef,
  navProps,
  after,
  action,
  end,
  tabProps,
  wrapTab,
  holdFit = false,
  rowProps,
}: RecordTabRowProps) {
  const phone = useIsPhoneViewport();
  const navNode = useRef<HTMLElement | null>(null);
  const attachNav = useCallback(
    (node: HTMLElement | null) => {
      navNode.current = node;
      if (typeof navRef === "function") navRef(node);
      else if (navRef)
        (navRef as MutableRefObject<HTMLElement | null>).current = node;
    },
    [navRef]
  );

  // The tabs the desktop strip has no room for, measured (#14753). The active tab is never one of
  // them, whatever the last measure said: selecting a tab draws it in the same render.
  const [measured, setMeasured] = useState<readonly string[]>(NO_KEYS);
  const overflowed: readonly string[] = phone
    ? NO_KEYS
    : measured.filter(
        (key) => key !== active && tabs.some((tab) => tab.key === key)
      );
  const overflowedSet = new Set(overflowed);
  const drawnKeys: string[] = tabs
    .map((tab) => tab.key)
    .filter((key) => !overflowedSet.has(key));
  const keys = tabs.map((tab) => tab.key).join("\n");
  const texts = tabs
    .map((tab) => `${tab.label}\u0001${tab.count ?? ""}`)
    .join("\n");
  const hasAction = Boolean(action);
  // Run again when a hold ends (a new `fit`), so the row catches up with what changed meanwhile.
  const fit = useCallback(() => {
    const strip = navNode.current;
    if (!strip || holdFit) return;
    const rooms: TabRoom[] = [];
    for (const key of keys === "" ? [] : keys.split("\n")) {
      const button = strip.querySelector<HTMLElement>(
        `[data-record-tab="${CSS.escape(key)}"]`
      );
      if (button) rooms.push({ key, room: tabRoom(button, strip) });
    }
    const next = overflowedTabKeys(
      rooms,
      active,
      strip.getBoundingClientRect().width,
      roomOf(
        strip.querySelector<HTMLElement>(":scope > [data-record-tab-more]")
      ),
      roomOf(
        strip.querySelector<HTMLElement>(":scope > [data-record-tab-action]")
      )
    );
    setMeasured((current) => (sameKeys(current, next) ? current : next));
  }, [keys, active, holdFit]);
  // Measured before paint whenever what the strip holds changes: its tabs, their labels and
  // counts, the active tab and the action.
  useLayoutEffect(() => {
    if (!phone) fit();
  }, [fit, phone, texts, hasAction]);
  // And whenever the strip or anything in it changes size: the window, the frame, the end beside
  // it, a tab's badge.
  useEffect(() => {
    const strip = navNode.current;
    if (phone || !strip) return;
    const observer = new ResizeObserver(() => fit());
    observer.observe(strip);
    strip
      .querySelectorAll<HTMLElement>(
        "[data-record-tab], :scope > [data-record-tab-more], :scope > [data-record-tab-action]"
      )
      .forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [fit, phone, hasAction]);

  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const moreOpen = moreAnchor !== null && overflowed.length > 0;
  // A menu emptied by a wider row closes for good: a narrower row brings More back closed, for
  // the menu opens only by its control (#14753).
  const nothingUnder = overflowed.length === 0;
  useLayoutEffect(() => {
    if (nothingUnder) setMoreAnchor(null);
  }, [nothingUnder]);
  const moreItems: MenuAction[] = tabs
    .filter((tab) => overflowedSet.has(tab.key))
    .map((tab) => ({
      label: tab.label,
      note:
        [tab.count !== undefined ? String(tab.count) : null, tab.note ?? null]
          .filter((part): part is string => part !== null)
          .join(" · ") || undefined,
      onSelect: () => {
        setMoreAnchor(null);
        onSelect(tab.key);
      },
    }));

  const wrapTabs = (items: ReactNode): ReactNode =>
    tablist ? tablistBox(label, items) : items;
  const tabButtons = wrapTabs(
    tabs.map((tab, index) => {
      const isActive = tab.key === active;
      const isOverflowed = overflowedSet.has(tab.key);
      const { style: extraStyle, ...extra } = tabProps?.(tab, isActive) ?? {};
      const button = (
        <button
          key={tab.key}
          type="button"
          onClick={() => onSelect(tab.key)}
          /* Which tab you are on is stated semantically rather than
           only in the underline, so it survives a restyle and is
           announced rather than merely drawn. */
          aria-current={isActive ? "page" : undefined}
          /* Sentence case, not shouted caps: the tab strip is
           navigation, and uppercase spends emphasis the page needs
           for the record itself (#11426). */
          className="inline-flex items-center gap-1.5"
          data-record-tab={tab.key}
          {...extra}
          {...(isOverflowed
            ? {
                [RECORD_TAB_OVERFLOWED_ATTRIBUTE]: "",
                "aria-hidden": true,
                tabIndex: -1,
              }
            : null)}
          style={{
            ...recordTabStyle(isActive, tab.faint === true),
            // On a phone the gap before the pinned action is the action's own (round 42
            // review), so it holds while the strip is cut; the last tab's would double it at
            // rest. On desktop the action follows the last tab inline, after the tab's own gap.
            ...(phone && action && index === tabs.length - 1
              ? { marginRight: 0 }
              : null),
            ...extraStyle,
            ...(isOverflowed ? recordTabOverflowedStyle : null),
          }}
        >
          {tab.label}
          {/* A real space, so the accessible name and the copied text
            read "Facts 4" rather than "Facts4". The flex gap does
            the visual spacing; this is for the text layer. */}
          {tab.count !== undefined ? " " : null}
          {tab.count !== undefined ? (
            <span style={tabCountStyle}>{tab.count}</span>
          ) : null}
          {tab.trail}
        </button>
      );
      return wrapTab
        ? wrapTab(tab, button, index, isOverflowed, drawnKeys)
        : button;
    })
  );
  if (!phone)
    return (
      /* Desktop (#14753, superseding the wrapping of #14339 section 3; Gerry, round 27: "really
         don't like how we fade the tabs"): one line, and no tab is ever cut or faded. The strip
         takes the row's width less the end's; the tabs that fit are drawn in order, the active
         one always, and the rest are listed under More, which follows the last drawn tab, before
         the action. Every tab stays mounted, an overflowed one held out of the flow, so a wider
         row draws it again. The end is on the tabs' text line, right-aligned, the wide gap after
         the strip. */
      <div
        className="mb-2 flex items-baseline"
        data-record-tabs=""
        {...rowProps}
        style={{
          boxShadow: "inset 0 -1px 0 var(--fp-border)",
          ...rowProps?.style,
        }}
      >
        <nav
          ref={attachNav}
          aria-label={tablist ? undefined : label}
          className="flex whitespace-nowrap"
          data-record-tab-strip=""
          // Relative: an overflowed tab is held at the strip's own corner, inside it.
          style={{
            flex: "1 1 0",
            minWidth: 0,
            position: "relative",
            ...navStyle,
          }}
          {...navProps}
        >
          {tabButtons}
          {after}
          <div
            className="flex flex-none items-stretch"
            data-record-tab-more=""
            // Mounted while every tab fits too, held as an overflowed tab is, so its room is
            // known the moment a tab stops fitting. A box of its own, as the action's is: the
            // strip's own buttons are its tabs.
            style={{
              order: MORE_ORDER,
              ...(overflowed.length === 0 ? recordTabOverflowedStyle : null),
            }}
          >
            <button
              type="button"
              className="inline-flex items-center gap-1.5"
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              onClick={(event) => setMoreAnchor(event.currentTarget)}
              {...(overflowed.length === 0
                ? { "aria-hidden": true, tabIndex: -1 }
                : null)}
              style={{
                ...recordTabStyle(false, false),
                background: "none",
                cursor: "pointer",
                ...(moreOpen ? { color: "var(--fp-ink)" } : null),
              }}
            >
              {/* No count (Hunter, 2026-10-28, variant B): beside tabs whose numbers are record
                  counts, a number on More would read as one. The menu's items keep theirs. */}
              More
              <CaretDown size={12} aria-hidden />
            </button>
          </div>
          {action ? (
            <div
              className="flex flex-none items-stretch"
              data-record-tab-action=""
              // After every tab whatever order a host draws them in (the editor's carried
              // tab and its slot take `order`).
              style={{ order: ACTION_ORDER }}
            >
              {action}
            </div>
          ) : null}
        </nav>
        {end ? (
          <div
            className="flex flex-none"
            data-record-tab-end=""
            style={{ marginLeft: "var(--fp-gap-wide)" }}
          >
            {end}
          </div>
        ) : null}
        <Menu
          anchorEl={moreAnchor}
          open={moreOpen}
          onClose={() => setMoreAnchor(null)}
          label="More tabs"
          items={moreItems}
        />
      </div>
    );
  return (
    /* Phone. The row draws the strip's rule (an inset line at its foot), under the tabs, the action
       and the gap before the end alike; the active tab's 2px underline sits on it. The strip
       takes the tabs' width and no more, scrolling quietly in its own box with the edge fade
       when they outrun it (#13370 round 42: no painted bar on an inner region, and nothing
       running under the end); the action follows it, always in view. overflow-y-hidden keeps
       `overflow-x-auto` from promoting the other axis to `auto` (#10937). */
    <div
      className="mb-2 flex items-stretch"
      data-record-tabs=""
      {...rowProps}
      style={{
        boxShadow: "inset 0 -1px 0 var(--fp-border)",
        ...rowProps?.style,
      }}
    >
      <QuietScrollStrip
        className="min-w-0 flex-[0_1_auto] overflow-y-hidden"
        attributes={{ "data-record-tab-strip": "" }}
      >
        <nav
          ref={attachNav}
          aria-label={tablist ? undefined : label}
          className="flex whitespace-nowrap"
          style={navStyle}
          {...navProps}
        >
          {tabButtons}
          {after}
        </nav>
      </QuietScrollStrip>
      {action ? (
        <div
          className="flex flex-none items-stretch"
          data-record-tab-action=""
          // The tabs' own gap, kept when the strip is cut and its last tab fades out under the
          // edge: the action never runs into it (round 42 review: "Fact+ Add…" at 390).
          style={{ marginLeft: "var(--fp-gap-wide)" }}
        >
          {action}
        </div>
      ) : null}
      <div className="min-w-0 flex-1" aria-hidden />
      {end}
    </div>
  );
}
